import {
  getLogger,
  LogCategory,
  LogEvent,
  LogLevel,
} from '../../infrastructure/logger';

import type { DataFetchDiagnosticContext } from './DataFetchError';
import { isDataFetchError } from './DataFetchError';
import { HttpError } from './HTTPErrors';
import { sanitizeError } from './sanitizeError';

const logger = getLogger('error-diagnostics');

/** Flat diagnostic fields shown in support UI and attached to Sentry events. */
export type ErrorDiagnostics = {
  errorName: string;
  errorMessage: string;
  httpStatus?: number;
  requestId?: string;
  queryName?: string;
  queryArgs?: unknown;
  fetchStatus?: string;
  queryStatus?: string;
  isFetching?: boolean;
  isLoading?: boolean;
  failureReason?: string;
  pageUrl?: string;
  timestamp?: string;
  userAgent?: string;
};

/** Query-arg keys safe to expose in production UI / console (IDs and paging only). */
const PUBLIC_QUERY_ARG_KEYS = new Set([
  'id',
  'bpmnProcessId',
  'limit',
  'offset',
  'cursor',
  'context',
  'groupBy',
  'configId',
  'selectMode',
  'pagingEnabled',
  'pageIndex',
  'pageSize',
]);

function redactPageUrl(url: string): string {
  try {
    const parsed = new URL(url);
    return parsed.pathname;
  } catch {
    return '[REDACTED URL]';
  }
}

function redactQueryArgsForPublic(queryArgs: unknown): unknown {
  if (queryArgs === undefined || queryArgs === null) {
    return queryArgs;
  }

  if (typeof queryArgs !== 'object' || Array.isArray(queryArgs)) {
    return '[REDACTED]';
  }

  const entries = Object.entries(queryArgs as Record<string, unknown>);
  const redacted: Record<string, unknown> = {};
  const redactedKeys: string[] = [];

  for (const [key, value] of entries) {
    if (PUBLIC_QUERY_ARG_KEYS.has(key)) {
      redacted[key] = value;
    } else {
      redactedKeys.push(key);
    }
  }

  if (redactedKeys.length > 0) {
    redacted['_redactedKeys'] = redactedKeys;
  }

  return sanitizeError(redacted);
}

/**
 * Returns diagnostics safe for production console output, support UI, and session
 * replays. Sensitive query args and URL query strings are always redacted.
 */
export function getSafeErrorDiagnostics(
  diagnostics: ErrorDiagnostics,
): ErrorDiagnostics {
  return {
    errorName: diagnostics.errorName,
    errorMessage: diagnostics.errorMessage,
    httpStatus: diagnostics.httpStatus,
    requestId: diagnostics.requestId,
    queryName: diagnostics.queryName,
    queryArgs:
      diagnostics.queryArgs !== undefined
        ? redactQueryArgsForPublic(diagnostics.queryArgs)
        : undefined,
    fetchStatus: diagnostics.fetchStatus,
    queryStatus: diagnostics.queryStatus,
    isFetching: diagnostics.isFetching,
    isLoading: diagnostics.isLoading,
    failureReason: diagnostics.failureReason,
    pageUrl: diagnostics.pageUrl
      ? redactPageUrl(diagnostics.pageUrl)
      : undefined,
    timestamp: diagnostics.timestamp,
    userAgent: diagnostics.userAgent,
  };
}

/**
 * Minimal error shape for production console logging (no stack / nested payloads).
 */
export function getConsoleSafeErrorSummary(
  error: Error,
): Record<string, unknown> {
  const summary: Record<string, unknown> = {
    name: error.name,
    message: error.message,
  };

  if (error instanceof HttpError) {
    summary['code'] = error.code;
    if (error.requestId) {
      summary['requestId'] = error.requestId;
    }
  }

  return summary;
}

/**
 * Logs session-replay-safe diagnostics via structured logging in all environments.
 * Local development additionally attaches the raw Error for debugging.
 */
export function logErrorDiagnosticsToConsole(
  prefix: string,
  error: Error,
  diagnostics: ErrorDiagnostics,
): void {
  const formatted = formatErrorDiagnostics(
    getSafeErrorDiagnostics(diagnostics),
  );

  logger.contextualLog(LogLevel.ERROR, prefix, undefined, {
    category: LogCategory.API,
    event: LogEvent.API_ERROR,
    error,
    errorSummary: getConsoleSafeErrorSummary(error),
    diagnostics: formatted,
    ...(diagnostics.requestId ? { requestId: diagnostics.requestId } : {}),
    ...(diagnostics.queryName ? { queryName: diagnostics.queryName } : {}),
  });
}

function getPageUrl(): string | undefined {
  if (typeof globalThis.location === 'undefined') {
    return undefined;
  }
  return globalThis.location.href;
}

function getUserAgent(): string | undefined {
  if (typeof globalThis.navigator === 'undefined') {
    return undefined;
  }
  return globalThis.navigator.userAgent;
}

function fromDataFetchContext(
  context: DataFetchDiagnosticContext,
): ErrorDiagnostics {
  return {
    errorName: 'DataFetchError',
    errorMessage: context.reason,
    queryName: context.queryName,
    queryArgs: context.queryArgs,
    fetchStatus: context.fetchStatus,
    queryStatus: context.queryStatus,
    isFetching: context.isFetching,
    isLoading: context.isLoading,
    failureReason: context.reason,
    pageUrl: context.pageUrl,
    timestamp: context.timestamp,
    userAgent: getUserAgent(),
  };
}

/**
 * Extracts structured diagnostics from an error for UI display and Sentry.
 */
export function getErrorDiagnostics(error: unknown): ErrorDiagnostics {
  const base: ErrorDiagnostics = {
    errorName: error instanceof Error ? error.name : 'UnknownError',
    errorMessage:
      error instanceof Error ? error.message : String(error ?? 'Unknown error'),
    pageUrl: getPageUrl(),
    timestamp: new Date().toISOString(),
    userAgent: getUserAgent(),
  };

  if (isDataFetchError(error)) {
    return {
      ...fromDataFetchContext(error.diagnostics),
      errorMessage: error.message,
      httpStatus: error.code,
      requestId: error.requestId,
    };
  }

  if (error instanceof HttpError) {
    return {
      ...base,
      errorName: error.name,
      errorMessage: error.message,
      httpStatus: error.code,
      requestId: error.requestId,
    };
  }

  return base;
}

/**
 * Formats diagnostics as multi-line text for technical-details panels and console output.
 */
export function formatErrorDiagnostics(diagnostics: ErrorDiagnostics): string {
  const lines: string[] = [
    `Error: ${diagnostics.errorName}`,
    `Message: ${diagnostics.errorMessage}`,
  ];

  if (diagnostics.httpStatus !== undefined) {
    lines.push(`HTTP status: ${diagnostics.httpStatus}`);
  }
  if (diagnostics.requestId) {
    lines.push(`Request / support ID: ${diagnostics.requestId}`);
  }
  if (diagnostics.queryName) {
    lines.push(`Query: ${diagnostics.queryName}`);
  }
  if (diagnostics.queryArgs !== undefined) {
    try {
      lines.push(
        `Query args: ${JSON.stringify(diagnostics.queryArgs, null, 2)}`,
      );
    } catch {
      lines.push('Query args: [non-serializable]');
    }
  }
  if (diagnostics.fetchStatus) {
    lines.push(`Fetch status: ${diagnostics.fetchStatus}`);
  }
  if (diagnostics.queryStatus) {
    lines.push(`Query status: ${diagnostics.queryStatus}`);
  }
  if (diagnostics.isFetching !== undefined) {
    lines.push(`Is fetching: ${diagnostics.isFetching}`);
  }
  if (diagnostics.isLoading !== undefined) {
    lines.push(`Is loading: ${diagnostics.isLoading}`);
  }
  if (diagnostics.failureReason) {
    lines.push(`Failure reason: ${diagnostics.failureReason}`);
  }
  if (diagnostics.pageUrl) {
    lines.push(`Page URL: ${diagnostics.pageUrl}`);
  }
  if (diagnostics.timestamp) {
    lines.push(`Timestamp: ${diagnostics.timestamp}`);
  }
  if (diagnostics.userAgent) {
    lines.push(`User agent: ${diagnostics.userAgent}`);
  }

  return lines.join('\n');
}

/**
 * Sanitized diagnostics object suitable for Sentry `extra` / `contexts`.
 */
export function getSentryErrorContext(error: unknown): Record<string, unknown> {
  const diagnostics = getSafeErrorDiagnostics(getErrorDiagnostics(error));
  return sanitizeError({
    ...diagnostics,

    ...(error instanceof Error && error.stack ? { stack: error.stack } : {}),
    ...(isDataFetchError(error)
      ? {
          dataFetchDiagnostics: {
            ...error.diagnostics,
            queryArgs: redactQueryArgsForPublic(error.diagnostics.queryArgs),
            pageUrl: error.diagnostics.pageUrl
              ? redactPageUrl(error.diagnostics.pageUrl)
              : undefined,
          },
        }
      : {}),
  }) as Record<string, unknown>;
}
